/**
 * Action Delegation — SL-UI05 compliant event handling.
 *
 * Replaces inline onclick="fn(...)" HTML attributes with data-action/data-arg
 * data attributes + a single document-level click listener.
 *
 * Usage:
 *   <button data-action="navigate" data-arg="/run/abc">Go</button>
 *   <button data-action="copyToClip" data-arg="some text">Copy</button>
 *
 * Initialize once in app.ts after window globals are set:
 *   import { initActionDelegation } from './galaxy/components/action-delegation.js'
 *   initActionDelegation()
 */

/** Registry of named action handlers. UIEvent is the common base for MouseEvent and KeyboardEvent. */
type ActionHandler = (arg: string, event: UIEvent) => void

const _actions = new Map<string, ActionHandler>()

/**
 * Register an action handler. Idempotent — later calls win.
 * Handlers are invoked as: handler(dataArg, clickEvent)
 */
export function registerAction(name: string, handler: ActionHandler): void {
  _actions.set(name, handler)
}

/** Initialize document-level click and keyboard delegation. Call once on app mount. */
export function initActionDelegation(): void {
  document.addEventListener('click', (e) => {
    const el = (e.target as Element).closest('[data-action]')
    if (!(el instanceof HTMLElement)) {
      return
    }

    const action = el.dataset.action
    if (!action) {
      return
    }

    const arg = el.dataset.arg ?? ''
    const handler = _actions.get(action)
    if (handler) {
      e.stopPropagation()
      handler(arg, e)
    }
  })

  // Keyboard activation for all data-action elements (SL-UA03)
  // role=link: Enter only — Space is reserved for browser scroll (convention-f4770a53a461)
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') {
      return
    }
    const el = (e.target as Element).closest('[data-action]')
    if (!(el instanceof HTMLElement)) {
      return
    }
    if (e.key === ' ' && el.getAttribute('role') === 'link') {
      return
    }

    const action = el.dataset.action
    if (!action) {
      return
    }

    const arg = el.dataset.arg ?? ''
    const handler = _actions.get(action)
    if (handler) {
      e.preventDefault()
      e.stopPropagation()
      handler(arg, e)
    }
  })
}

/** Helper: emit a data-action attribute string for use in template literals. */
export function action(name: string, arg?: string): string {
  return arg !== undefined
    ? `data-action="${name}" data-arg="${String(arg).replace(/"/g, '&quot;')}"`
    : `data-action="${name}"`
}
